/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useServiceContext } from '../../root'
import { useRcmTableData } from '../../hooks/useRcmTableData'

const DEFAULT_PER_PAGE = 15

interface RcmArrowsPaginateProps {
  perPageSizes?: number[]
}

export const RcmArrowsPaginate: React.FC<RcmArrowsPaginateProps> = ({ perPageSizes = [10, 15, 25, 50, 100, 200] }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableData = useRcmTableData()

  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)

  useEffect(() => {
    // Load persisted pagination settings
    if (tableData.persistenceKey) {
      const persistedPaginateSettings = context.service.persist.getGlobal<{
        page: number
        perPage: number
      }>('paginate')
      const pageSize =
        persistedPaginateSettings && persistedPaginateSettings.perPage ? persistedPaginateSettings.perPage : null

      if (pageSize) {
        setPerPage(pageSize)
      }
    }
  }, [])

  const handlePaginate = (newPage: number, newPerPage: number) => {
    setPerPage(newPerPage)

    if (tableData.persistenceKey) {
      context.service.persist.setGlobal('paginate', {
        page: newPage,
        perPage: newPerPage
      })
    }

    // Calculate pageStart (0-indexed) from page (1-indexed)
    tableData.load({
      sortDesc: tableData.defaultSortDesc || false,
      sortBy: tableData.defaultSortBy || 'id',
      pageSize: newPerPage,
      pageStart: newPage - 1
    })
  }

  const currentPage = tableData.page
  const hasNextPage = tableData.hasNextPage

  return (
    <div className="paginate clearfix">
      <ul className="controls">
        <li>
          <a
            className={'previous' + (currentPage === 1 ? ' disabled' : '')}
            style={{ padding: 0 }}
            onClick={(e) => {
              e.preventDefault()
              if (currentPage > 1) {
                handlePaginate(currentPage - 1, perPage)
              }
            }}
          >
            <i className="fa fa-angle-left" />
          </a>
        </li>
        <li className="ml10">
          <a
            className={'next' + (!hasNextPage ? ' disabled' : '')}
            style={{ padding: 0 }}
            onClick={(e) => {
              e.preventDefault()
              if (hasNextPage) {
                handlePaginate(currentPage + 1, perPage)
              }
            }}
          >
            <i className="fa fa-angle-right" />
          </a>
        </li>
      </ul>
      <div className="page-size-configurator">
        <div className="per-page">
          <span>{translate('PAGINATION_PER_PAGE')}</span>
          <select
            name="itemsPerPage"
            className="form-control"
            value={perPage}
            onChange={(event) => {
              handlePaginate(1, Number(event.target.value))
            }}
          >
            {perPageSizes.map((pageLength) => (
              <option key={pageLength} value={pageLength}>
                {pageLength}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}
